import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SiteSurveyResponseClient, SiteSurveyResponseDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new SiteSurveyResponseClient(API_BASE_URL, axiosInstance);

// Query hook - Get all sitesurveyresponses for dropdowns (cached 4 hours)
export const useSiteSurveyResponses = () => {
  return useQuery({
    queryKey: ['sitesurveyresponses'],
    queryFn: async () => {
      const response = await apiClient.searchSiteSurveyResponses({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const useSiteSurveyResponseSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchSiteSurveyResponses({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single siteSurveyResponse by ID
export const useSiteSurveyResponseById = (id: string) => {
  return useQuery({
    queryKey: ['sitesurveyresponses', id],
    queryFn: async () => {
      const response = await apiClient.getSiteSurveyResponseById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create siteSurveyResponse
export const useCreateSiteSurveyResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SiteSurveyResponseDTO) => {
      await apiClient.createSiteSurveyResponse(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sitesurveyresponses'] });
    },
  });
};

// Mutation hook - Update siteSurveyResponse
export const useUpdateSiteSurveyResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SiteSurveyResponseDTO }) => {
      await apiClient.updateSiteSurveyResponse(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sitesurveyresponses'] });
    },
  });
};

// Mutation hook - Delete siteSurveyResponse
export const useDeleteSiteSurveyResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteSiteSurveyResponse(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sitesurveyresponses'] });
    },
  });
};
